import { skipToken } from '@reduxjs/toolkit/dist/query';
import {
  useAddProductToCartMutation,
  useGetMachineByIdQuery,
  useGetPartByIdQuery,
  useGetProductByIdQuery,
  useAddProductToRemoteCartMutation
} from 'api';
import { Button, Flyout, Typography } from 'components';
import CustomLoader from 'components/CustomLoader';
import FlyoutFooter from 'components/Flyout/FlyoutFooter';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import theme from 'themes';
import { Error, FlyoutContainer, HeaderContainer, ViewContainer } from './PartViewFlyout.elements';
import { Product } from 'types/parts';
import { sfEnabled } from 'constants/featureFlags';
import { useCart, useUser } from 'selectors';
import { CartItem, CartState } from 'types/parts/cart';
import { Machine, User } from 'types';
import { UNIVERSAL_PRODUCTS_KEY } from 'constants/cart';
import { useDispatch } from 'react-redux';
import { cartActions } from 'actions';
import { generateAnalyticsEvent } from 'common/helpers/v1/analytics';
import { AnalyticsCategories, ECommerceAnalyticEventActions } from 'constants/analytics';
import X from 'icons/indexed-icons/X';
import PartDetailsView from './PartDetailsFlyout';
import { ToastMsg } from 'common/components/Toast';
import { ComposeCartItem } from '../CartHelp';

type Props = {
  machineId?: string;
  priceUnit?: string;
  productId?: string;
  handleFlyoutClose: () => void;
  open: boolean;
  isPurchasable?: boolean;
  isPartDiagram?: boolean;
  other?:Record<string, unknown>
};
const PartViewFlyout = ({
  machineId,
  priceUnit,
  productId,
  handleFlyoutClose,
  open = false,
  isPurchasable,
  isPartDiagram,
  other
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [addProductToCart, addCartResult] = useAddProductToCartMutation();
  const [AddProductToRemoteCart] = useAddProductToRemoteCartMutation();
  const cart = useCart() as CartState;
  const user = useUser() as User;

  const { t } = useTranslation(['fpns']);
  const [quantity, setQuantity] = useState(1);

  const {
    data: product,
    error: productError,
    isLoading: productIsLoading
  } = isPartDiagram && !isPurchasable
    ? useGetPartByIdQuery(productId ? productId : skipToken)
    : useGetProductByIdQuery({
        id: productId,
        machineUuid: machineId,
        priceUnit: priceUnit,
        custNum: other?.custNum,
        siteRef: other?.siteRef
      });

  const {
    data: machine
    // TODO - add warning and loading for machine info
    /*error: machineError,
        isLoading: machineIsLoading*/
  } = useGetMachineByIdQuery(machineId ? machineId : skipToken);

  useEffect(() => {
    generateAnalyticsEvent({
      category: AnalyticsCategories.ECOMMERCE,
      action: ECommerceAnalyticEventActions.VIEW_ITEM,
      label: product?.id
    });
  }, [product?.id]);

  const addToCart = () => {
    if (!product) {
      return;
    }
    try {
      if (sfEnabled()) {
        addProductToCart({
          // If the machine is given, use its plant ID, in case user has multiple plants
          accountId: machine ? machine.plantId : user.plants[0],
          cart: cart,
          product: product,
          productId: product.sfProductId as string,
          quantity: quantity,
          machineId: machine?.id as string,
          machineDescription:
            machine === undefined || machine?.id === UNIVERSAL_PRODUCTS_KEY
              ? UNIVERSAL_PRODUCTS_KEY
              : machine?.description
        });
      } else {

        AddProductToRemoteCart([
          ComposeCartItem(product, machineId as string, machine as Machine, quantity)
        ] as CartItem[])
            .unwrap()
            .then(() => {
            // maintain temporay FE cartState to track cart count
                dispatch({
                    type: cartActions.ADD_TO_CART,
                    item: product,
                    quantity: quantity,
                    groupId: machine?.id as string,
                    groupDescription:
                      machine === undefined || machine?.id === UNIVERSAL_PRODUCTS_KEY
                        ? UNIVERSAL_PRODUCTS_KEY
                        : machine?.description
               });
            })
              .catch((error) => {
                console.warn('Error adding item to cart: ', error);
              });
      }

      generateAnalyticsEvent({
        category: AnalyticsCategories.ECOMMERCE,
        action: ECommerceAnalyticEventActions.ADD_TO_CART_FROM_PDP,
        event: ECommerceAnalyticEventActions.ADD_TO_CART,
        currency: product.priceUnit,
        value: product.custPrice,
        items: [
          {
            item_id: product.id,
            item_name: product.description,
            quantity: product.stock,
            price: product.custPrice,
            currency: product.priceUnit
          }
        ]
      });
    } catch (error) {
      console.warn('Add to cart failed! Error:', error);
      ToastMsg({
        heading: 'Error',
        message: t('error_add_to_cart', { item: product.stockCode || product.sku, ns: 'fpns' }),
        type: 'error'
      });
    }
  };
  const handleQuantity = (quantity: number) => {
    setQuantity(quantity);
  };

  return (
    <FlyoutContainer>
      {open && (
        <Flyout width="25rem" visible={open} onClose={handleFlyoutClose}>
          {product ? (
            <ViewContainer>
              <Header handleFlyoutClose={handleFlyoutClose} product={product} quantity={quantity} />
              <PartDetailsView
                addCartResult={addCartResult}
                machine={machine}
                product={product}
                quantity={quantity}
                handleQuantity={handleQuantity}
              />
            </ViewContainer>
          ) : (
            <Error>
              {productIsLoading ? (
                <CustomLoader />
              ) : productError ? (
                <Typography color={theme.colors.text.error}>
                  {t('error_product_details')}
                </Typography>
              ) : (
                <Typography>{t('error_product')}</Typography>
              )}
            </Error>
          )}
          <FlyoutFooter
            rightBtn={t('add_to_cart') as string}
            leftBtn={t('cancel') as string}
            handleRightBtnClick={addToCart}
            disable={!product}
            handleFlyoutClose={handleFlyoutClose}
          />
        </Flyout>
      )}
    </FlyoutContainer>
  );
};

export default PartViewFlyout;

type HeaderProps = {
  product: Product;
  quantity?: number;
  handleFlyoutClose?: () => void;
};
function Header({ product, handleFlyoutClose }: HeaderProps) {
  return (
    <HeaderContainer>
      <Typography
        color={theme.colors.textLightBlack}
        className="product-code"
        variant="h2"
        style={{
          margin: 0
        }}
        // mb="0.5rem"
      >
        {`JBT #${product.stockCode || product.sku}`}
      </Typography>
      <Button onClick={handleFlyoutClose} className="close-btn" variant="text">
        <X color={theme.colors.textLightBlack} />
      </Button>
    </HeaderContainer>
  );
}
